import React, { Component } from 'react';
import html2canvas from 'html2canvas';
import { CUSTOM_TIME_RANGE_TYPES } from '@atlassian/jira-portfolio-3-common/src/date-manipulation/constants.tsx';
import { getInitialExportDateRange } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/title-bar/share-report/components/png-export/util';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { PROFILE_KEYS } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import {
	isExportPreview,
	getExportTimelineDateRange,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import ExportView from './export';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, MessageEvent, State } from './types';
import { preparePlanForExport } from './utils';

// eslint-disable-next-line jira/react/no-class-components
export default class App extends Component<Props, State> {
	// eslint-disable-next-line react/sort-comp
	isExportTestMode: boolean;

	replaceWithExport: boolean;

	originalOverflowX?: string;

	originalOverflowY?: string;

	hasDoneInitialLoad = false;

	hasSentInitialLoadWithIssuesProfile = false;

	// the full list of props is available here: https://html2canvas.hertzen.com/configuration
	// (you might need to upgrade to the latest version to use some props)
	html2CanvasConfig = {
		allowTaint: true,
		useCORS: true,
		taintTest: false,
		scale: 1,
		logging: false,
	};

	// eslint-disable-next-line react/sort-comp
	state = {
		timelineDateRange: undefined,
	};

	constructor(props: Props) {
		super(props);
		// Show the exported image directly underneath the plan with an "Update" button to re-export
		this.isExportTestMode = window?.location.href.indexOf('exportTestMode=1') >= 0;
		// Replace the document body content with the export once it's done (used for storybook VR test)
		this.replaceWithExport = window?.location.href.indexOf('replaceWithExport=1') >= 0;
	}

	// Send a profiling event to the iframe parent
	// The parent will call `monitor.startProfile` or `monitor.finishProfile` and they'll
	// automatically be recorded against the running task (initialPreview|subsequentPreview|export)
	sendProfileEvent = (profileAction: 'start' | 'finish', profileKey: string) => {
		window.parent?.postMessage(
			{
				type: 'profile',
				payload: {
					profileAction,
					profileKey,
				},
			},
			'*',
		);
	};

	messageHandler = async (event: MessageEvent) => {
		if (event.origin !== window.location.origin) {
			return;
		}
		const postExportedImage = async (
			eventType: 'exportedImage' | 'fullExportedImage' = 'exportedImage',
		) => {
			if (this.props.numIssues > 0) {
				// requestAnimationFrame ensures the browser has finished painting before we do the export
				// https://stackoverflow.com/a/69529699
				requestAnimationFrame(() => {
					setTimeout(async () => {
						const imageData = await this.exportView();
						event.source.postMessage(
							{
								type: eventType,
								payload: {
									image: imageData,
								},
							},
							event.origin,
						);
					}, 1);
				});
			}
		};

		switch (event.data.type) {
			case 'getExportedImage': {
				postExportedImage();
				break;
			}
			case 'updateTimelineWidth': {
				const { fieldsWidth, scopeWidth, timelineExport } = event.data.payload;
				this.props.updateColumnWidths(scopeWidth, fieldsWidth, 0, timelineExport);
				if (isExportPreview()) {
					postExportedImage();
				}
				break;
			}
			case 'updateTimelineDateRange': {
				const { timelineDateRange } = event.data.payload;
				this.setState({
					timelineDateRange,
				});
				this.props.updateTimelineDateRange(timelineDateRange);

				if (isExportPreview()) {
					postExportedImage();
				} else {
					// For some reason, handleOnloadComponent is not called when timeline date range is updated
					// So, here we need to send message to the png-export that the download mode is ready
					requestAnimationFrame(() => {
						setTimeout(() => {
							window.parent.postMessage(
								{
									type: 'downloadModeReady',
								},
								'*',
							);
						}, 1);
					});
				}
				break;
			}
			case 'downloadImage': {
				if (!isExportPreview()) {
					postExportedImage('fullExportedImage');
				}
				break;
			}
			default:
				break;
		}
	};

	componentDidMount() {
		this.props.initialLoad({ locale: this.props.locale });
		this.props.setInitialFilterValues();

		// Make sure you can scroll around the export view
		this.originalOverflowX = window.document.body.style.overflowX;
		this.originalOverflowY = window.document.body.style.overflowY;
		window.document.body.style.setProperty('overflow-y', 'auto', 'important');
		window.document.body.style.setProperty('overflow-x', 'auto', 'important');

		// @ts-expect-error - TS2769 - No overload matches this call.
		window.addEventListener('message', this.messageHandler);

		// In test mode we do some crude dom stuff to show the image below the plan
		if (this.isExportTestMode && !this.replaceWithExport) {
			const btn = window.document.createElement('button');
			btn.onclick = () => {
				const el = window.document.getElementById('export-preview');
				if (el) {
					el.innerHTML = '';
				}
				this.showPreviewBelow();
			};
			btn.innerHTML = 'Update';
			window.document.body.appendChild(btn);
			const div = window.document.createElement('div');
			div.id = 'export-preview';
			window.document.body.appendChild(div);
		}
	}

	componentDidUpdate() {
		if (this.props.numIssues > 0) {
			this.sendProfileEvent('finish', PROFILE_KEYS.EXPORT_PNG.REACT_RENDER);
		}
	}

	// Only used in test mode
	showPreviewBelow() {
		requestAnimationFrame(() => {
			setTimeout(() => {
				this.exportView().then((dataUrl) => {
					const container = this.replaceWithExport
						? window.document.querySelector('.arj-export-view')
						: window.document.getElementById('export-preview');
					const img = new Image();
					img.src = dataUrl;
					img.setAttribute(
						'data-testid',
						'portfolio-3-portfolio.app-export-view.view.main.export-image',
					);

					if (container) {
						container.innerHTML = '';
						container.appendChild(img);
					}
				});
				// delay the exportView after the initial load to make sure all the issues has been loaded.
			}, 3000);
		});
	}

	handleOnloadComponent = async () => {
		if (this.props.numIssues > 0 && !this.hasDoneInitialLoad) {
			this.hasDoneInitialLoad = true;

			if (this.isExportTestMode) {
				this.showPreviewBelow();
			}

			// Only initialise timeline range on the first load of export view, ie. when timelineDateRange is undefined
			if (!isDefined(this.state.timelineDateRange)) {
				const { fromDate, toDate } = getExportTimelineDateRange();
				const initialTimelineDateRange =
					isDefined(fromDate) && isDefined(toDate)
						? {
								fromDate,
								toDate,
								typeOfCustomDateRange: CUSTOM_TIME_RANGE_TYPES.FIXED,
							}
						: getInitialExportDateRange();
				this.setState({
					timelineDateRange: initialTimelineDateRange,
				});
				this.props.updateTimelineDateRange(initialTimelineDateRange);
			}
			// calling setTimeout here to make sure exportView is run after the updateTimelineDateRange,
			// otherwise the timeline header is not rendered correctly
			if (isExportPreview()) {
				requestAnimationFrame(() => {
					setTimeout(async () => {
						const imageData = await this.exportView();

						window.parent.postMessage(
							{
								type: 'initialExportedImage',
								payload: {
									image: imageData,
								},
							},
							'*',
						);
					}, 1);
				});
			}
		}

		if (!isExportPreview()) {
			window.parent.postMessage(
				{
					type: 'downloadModeReady',
				},
				'*',
			);
		}
	};

	componentWillUnmount() {
		// Remove the inline styles
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		window.document.body.style.overflowX = this.originalOverflowX!;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		window.document.body.style.overflowY = this.originalOverflowY!;

		// @ts-expect-error - TS2769 - No overload matches this call.
		window.removeEventListener('message', this.messageHandler);
	}

	getExportViewNode = () =>
		document.querySelector<HTMLElement>('.arj-export-view') || document.body;

	exportView = async () => {
		this.sendProfileEvent('start', PROFILE_KEYS.EXPORT_PNG.PREPARE_FOR_EXPORT);
		preparePlanForExport();
		this.sendProfileEvent('finish', PROFILE_KEYS.EXPORT_PNG.PREPARE_FOR_EXPORT);
		this.sendProfileEvent('start', PROFILE_KEYS.EXPORT_PNG.CANVAS_RENDER);

		let data;
		const exportView = this.getExportViewNode();

		if (exportView && exportView.scrollHeight > 10000) {
			data = await this.convertToCanvasOnLargePlan();
		} else {
			data = await this.convertToCanvas();
		}
		this.sendProfileEvent('finish', PROFILE_KEYS.EXPORT_PNG.CANVAS_RENDER);
		return data;
	};

	convertToCanvas = async () => {
		const exportView = this.getExportViewNode();
		const config = {
			...this.html2CanvasConfig,
			windowWidth: this.props.appWidth,
			windowHeight: exportView?.scrollHeight,
		};
		const canvas = await html2canvas(exportView, config);
		return canvas.toDataURL();
	};

	convertToCanvasOnLargePlan = async () => {
		const exportView = this.getExportViewNode();
		const canvasWidth = this.props.appWidth;
		const canvas2Height = exportView ? Math.round(exportView.scrollHeight) : 0;
		const canvas1Height = canvas2Height / 2;

		const canvas1Config = {
			...this.html2CanvasConfig,
			windowWidth: canvasWidth,
			windowHeight: canvas1Height,
			height: canvas1Height,
		};

		const canvas2Config = {
			...this.html2CanvasConfig,
			windowWidth: canvasWidth,
			windowHeight: canvas2Height,
			y: canvas1Height,
			height: canvas2Height - canvas1Height,
		};

		const [canvas1, canvas2] = await Promise.all([
			html2canvas(exportView, canvas1Config),
			html2canvas(exportView, canvas2Config),
		]);

		const canvas = document.createElement('canvas');
		canvas.setAttribute('width', canvasWidth.toString());
		canvas.setAttribute('height', canvas2Height.toString());
		const ctx = canvas.getContext('2d');
		ctx?.drawImage(canvas1, 0, 0);
		ctx?.drawImage(canvas2, 0, canvas1Height);
		return canvas.toDataURL();
	};

	render() {
		if (this.props.numIssues > 0) {
			// This is our best approximation at timing the initial load (before the first render that includes issues)
			if (!this.hasSentInitialLoadWithIssuesProfile) {
				this.sendProfileEvent('finish', PROFILE_KEYS.EXPORT_PNG.INITIAL_LOAD);
				this.hasSentInitialLoadWithIssuesProfile = true;
			}
			this.sendProfileEvent('start', PROFILE_KEYS.EXPORT_PNG.REACT_RENDER);
		}
		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div className={styles.app} data-name="ManualPlans">
				<ExportView onLoadComplete={this.handleOnloadComponent} appWidth={this.props.appWidth} />
			</div>
		);
	}
}
